import type { CSSProperties } from '@material-ui/styles'
import type { EditorProps } from './Editor'

/**
 * An editor is "inline" when it renders bare inside surrounding content with a bubble/floating
 * menu instead of the boxed toolbar look — currently only the Presentation v2 `HtmlEditor`.
 */
export const isInlineEditor = (props: Pick<EditorProps, 'bubbleMenuEnabled' | 'floatingMenuEnabled'>): boolean =>
  Boolean(props.bubbleMenuEnabled || props.floatingMenuEnabled)

/**
 * ProseMirror appends `<br class="ProseMirror-trailingBreak">` to text blocks the browser would
 * otherwise render with no height (empty paragraphs) or collapse (blocks ending in a hard break).
 * That `<br>` is a rendering hack, not content, so it must not add a visible line of its own.
 *
 * - Boxed editors (email templates, notes, tasks, send-quote message…) hide it unconditionally.
 *   Showing it puts a phantom trailing newline at the end of the content (#22034).
 * - Inline pres-v2 editors keep it on genuinely *empty* paragraphs (`:only-child`) so a blank line
 *   keeps its height and stays clickable — otherwise the FloatingMenu can never be reached on a
 *   blank line (#21916).
 */
export const trailingBreakSelector = (inline: boolean): string =>
  inline ? '& .ProseMirror-trailingBreak:not(:only-child)' : '& .ProseMirror-trailingBreak'

/** Nest under the selector that owns the ProseMirror markup. See {@link trailingBreakSelector}. */
export const trailingBreakStyles = (inline: boolean): Record<string, CSSProperties> => ({
  [trailingBreakSelector(inline)]: {
    display: 'none'
  }
})
